/**
 * CommandCenterButton — the one prominent entry to the operator's own
 * Command Center, in BOTH navigation shells.
 *
 * Tapelligence gradient (blue #1E6FE0 → orange #F58220) with bold white
 * lettering, ≥44px tall, visible hover and pressed states, and the same
 * contrast in light and dark mode (the gradient is a solid, opaque surface).
 *
 * The LABEL IS NEVER HIDDEN on tablet or desktop (768 / 1024 / 1280 all show
 * the words); only phones below 768px fall back to the icon alone.
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Compass } from 'lucide-react';
import { useAuth } from '../../lib/auth';

const GRADIENT = 'linear-gradient(90deg, #1E6FE0 0%, #F58220 100%)';

export default function CommandCenterButton({ variant = 'topnav', onClick, testId }) {
    const { t } = useTranslation();
    const { hasPermission } = useAuth();
    const sidebar = variant === 'sidebar';

    // Same gate as the list entry: nobody is invited into a page the
    // backend would refuse them. Super admins always pass.
    if (!hasPermission('view_command_center')) return null;

    return (
        <NavLink
            to="/command-center"
            onClick={onClick}
            data-testid={testId || `${sidebar ? 'sidebar' : 'topnav'}-command-center-btn`}
            data-insight-id="nav.command-center-button"
            title={t('nav.commandCenter')}
            aria-label={t('nav.commandCenter')}
            className={({ isActive }) => [
                'group relative inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full',
                sidebar ? 'w-full justify-center px-4 py-2.5' : 'px-3 md:px-4 py-2',
                'font-bold text-white tracking-tight',
                'shadow-[0_2px_10px_rgba(30,111,224,0.35)] hover:shadow-[0_4px_16px_rgba(245,130,32,0.45)]',
                'transition-[filter,box-shadow,transform] duration-200',
                'hover:brightness-110 active:scale-[0.97] active:brightness-95',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                'focus-visible:ring-[#1E6FE0] focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900',
                isActive ? 'ring-2 ring-white/70 dark:ring-white/50 brightness-105' : '',
            ].join(' ')}
            style={{ background: GRADIENT, color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF' }}
        >
            <Compass className="w-[18px] h-[18px] shrink-0" strokeWidth={2.25} />
            {/* Label: hidden ONLY on phones (<768px). Visible at 768/1024/1280+. */}
            <span className={`${sidebar ? '' : 'hidden md:inline'} whitespace-nowrap text-sm`}>
                {t('nav.commandCenter')}
            </span>
        </NavLink>
    );
}
